import { eachDayOfInterval, format } from "date-fns"
import { formatInTimeZone } from "date-fns-tz"

import { captureSentryException } from "~root/sentry"

export const DEFAULT_TIMEZONE = "America/New_York"
const DATE_FORMAT_DEFAULT = "LLL do, yyyy"
export const DATE_FORMAT_SLASH = "MM/dd/yyyy"
export const FULL_MONTH_DATE_FORMAT = "MMMM d, yyyy"
const DEFAULT_TIME_FORMAT_WITH_TIMEZONE = "h:mm a (zzz)"
const DEFAULT_SHORT_DATE_FORMAT = "MMM d, yyyy"
const DEFAULT_SHORT_DATE_NO_YEAR_FORMAT = "MMM d"
const DEFAULT_CHECK_IN_DATE_FORMAT = DEFAULT_SHORT_DATE_FORMAT
const DEFAULT_CHECK_IN_TIME_FORMAT = `'at' ${DEFAULT_TIME_FORMAT_WITH_TIMEZONE}`
const DEFAULT_CHECK_IN_FORMAT = `${DEFAULT_CHECK_IN_DATE_FORMAT} ${DEFAULT_CHECK_IN_TIME_FORMAT}`

export function getShortDateNoYear(date: Date | number) {
    return format(date, DEFAULT_SHORT_DATE_NO_YEAR_FORMAT)
}

export function getUserReadableDate(date: Date | number, timeZone?: string) {
    if (timeZone) {
        formatInTimeZone(date, timeZone, DATE_FORMAT_DEFAULT)
    }
    return format(date, DATE_FORMAT_DEFAULT)
}

export function getFullFormattedDateTime(date: Date | number | string) {
    return formatInTimeZone(date, getLocalTimeZone(), DEFAULT_CHECK_IN_FORMAT)
}

export function getLocalTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
        captureSentryException(error)
        return DEFAULT_TIMEZONE
    }
}

export function formatBlogEntryDate(
    postDate: string | null | undefined
): string {
    return postDate ? getUserReadableDate(Date.parse(postDate)) : ""
}

/**
 * Generates an array of timestamps for each day within the given date range, inclusive.
 * @param startDate - The start date string (parsable by `new Date()`).
 * @param endDate - The end date string (parsable by `new Date()`).
 * @returns An array of numbers representing the timestamps for each day.
 * @returns Empty array if dates are invalid or if start date is after end date.
 */
export function getDateRange(
    startDate: string | Date,
    endDate: string | Date
): number[] {
    const start = new Date(startDate)
    const end = new Date(endDate)

    // Ensure dates are valid before proceeding
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return []
    }

    // Check if start date is after end date
    if (start > end) {
        return []
    }

    const dateArray = eachDayOfInterval({ start, end })
    return dateArray.map((date: Date) => date.getTime())
}

export function isMoreThan24HoursAway(date: number): boolean {
    const targetDate = new Date(date)
    const now = new Date()
    const diffInMs = targetDate.getTime() - now.getTime()
    return diffInMs > 24 * 60 * 60 * 1000
}
